// Static geo data used by `WorldMap`.
//
// Why hand-rolled instead of TopoJSON / react-simple-maps?
//   The page only needs a *low-fidelity* world outline (country shapes are
//   purely decorative). Shipping a vector world dataset (~100KB) or pulling
//   in `d3-geo` + `react-simple-maps` (~120KB combined) is overkill. The
//   data below is ~5KB minified, renders fast, and is easy to tweak.
//
// All polygons are arrays of [lon, lat] pairs. Country centroids and city
// coords are also [lon, lat]. The polygons are intentionally simplified —
// they approximate country shapes, they are not GIS-accurate.

export type LonLat = readonly [number, number];

// ---------------------------------------------------------------- country shells

export const COUNTRY_POLYS: Record<string, LonLat[]> = {
  CA: [[-141, 70], [-125, 70], [-95, 72], [-75, 73], [-65, 68], [-55, 60], [-58, 52], [-68, 50], [-80, 48], [-89, 48], [-95, 49], [-114, 49], [-123, 49], [-130, 55], [-141, 60]],
  US: [[-124, 49], [-95, 49], [-83, 46], [-67, 47], [-67, 44], [-70, 42], [-75, 39], [-77, 35], [-81, 32], [-81, 25], [-80, 25], [-82, 28], [-84, 30], [-89, 30], [-94, 29], [-97, 26], [-99, 29], [-104, 30], [-110, 31], [-117, 33], [-120, 35], [-124, 40]],
  MX: [[-117, 32], [-108, 32], [-103, 29], [-97, 25], [-97, 18], [-92, 17], [-88, 18], [-87, 21], [-89, 21], [-91, 18], [-95, 16], [-100, 17], [-104, 19], [-110, 23], [-115, 28]],
  BR: [[-74, 5], [-69, 1], [-60, 4], [-52, 4], [-50, 0], [-44, -1], [-37, -5], [-35, -9], [-39, -15], [-41, -22], [-48, -25], [-54, -30], [-58, -30], [-58, -22], [-64, -22], [-68, -12], [-70, -9], [-72, -7], [-73, -1]],
  AR: [[-65, -22], [-58, -24], [-55, -29], [-58, -35], [-62, -40], [-67, -46], [-70, -52], [-72, -54], [-71, -50], [-72, -45], [-72, -38], [-70, -30], [-68, -24]],
  CO: [[-79, 8], [-72, 12], [-67, 7], [-68, 3], [-71, -1], [-76, -1], [-78, 2]],
  PE: [[-81, -3], [-78, -9], [-72, -13], [-69, -16], [-71, -18], [-76, -15], [-81, -5]],
  CL: [[-71, -20], [-69, -25], [-71, -30], [-72, -38], [-73, -45], [-73, -50], [-75, -55], [-70, -55], [-69, -50], [-70, -40], [-71, -30], [-70, -25]],
  VE: [[-72, 11], [-66, 11], [-60, 8], [-62, 2], [-67, 2], [-72, 7]],
  GB: [[-5, 58], [-1, 57], [1, 53], [1, 52], [-1, 51], [-3, 51], [-5, 53], [-5, 55], [-6, 57]],
  IE: [[-10, 55], [-6, 55], [-6, 52], [-10, 52]],
  FR: [[-1, 49], [2, 51], [6, 49], [8, 48], [7, 45], [6, 43], [4, 43], [3, 42], [-1, 43], [-2, 46], [-4, 48]],
  DE: [[6, 54], [10, 54], [13, 54], [14, 51], [13, 48], [10, 48], [7, 49], [6, 51]],
  IT: [[7, 46], [12, 46], [13, 45], [14, 42], [18, 40], [18, 38], [16, 38], [14, 40], [8, 44]],
  ES: [[-9, 42], [-1, 43], [3, 42], [3, 39], [0, 36], [-5, 36], [-9, 37]],
  PT: [[-9, 42], [-7, 42], [-7, 37], [-9, 37]],
  PL: [[14, 54], [23, 54], [24, 49], [18, 49], [14, 51]],
  NL: [[4, 53], [7, 53], [6, 51], [4, 51]],
  BE: [[2, 51], [5, 51], [6, 50], [4, 50]],
  SE: [[11, 69], [17, 69], [24, 67], [20, 60], [18, 56], [12, 56], [11, 60]],
  NO: [[5, 62], [12, 69], [20, 71], [28, 71], [23, 66], [15, 60], [7, 59], [5, 60]],
  FI: [[20, 68], [28, 68], [30, 65], [28, 60], [22, 60], [20, 64]],
  RU: [[28, 68], [35, 68], [55, 69], [80, 72], [110, 72], [140, 70], [170, 70], [180, 66], [180, 60], [145, 55], [120, 55], [100, 52], [80, 50], [58, 52], [40, 55], [30, 60], [28, 65]],
  UA: [[22, 52], [32, 52], [40, 50], [40, 45], [32, 45], [24, 46], [22, 49]],
  TR: [[26, 42], [30, 42], [36, 42], [42, 40], [44, 37], [36, 37], [32, 36], [28, 36]],
  EG: [[25, 32], [34, 32], [34, 29], [36, 23], [33, 22], [25, 22]],
  LY: [[10, 33], [20, 33], [25, 32], [25, 22], [20, 20], [15, 23], [10, 30]],
  DZ: [[-8, 37], [0, 37], [8, 37], [10, 33], [5, 22], [-5, 22], [-8, 28]],
  SA: [[35, 32], [42, 32], [49, 28], [55, 22], [55, 18], [45, 17], [40, 17], [36, 19], [35, 25]],
  AE: [[51, 26], [54, 26], [56, 25], [57, 24], [54, 22], [52, 23]],
  IR: [[44, 39], [50, 38], [55, 38], [60, 37], [61, 30], [57, 25], [51, 28], [48, 30], [44, 33]],
  IQ: [[39, 37], [44, 37], [48, 32], [42, 30], [40, 32]],
  ZA: [[17, -29], [22, -32], [25, -34], [30, -33], [32, -27], [30, -23], [25, -22], [20, -25]],
  NG: [[3, 13], [10, 13], [14, 11], [14, 7], [10, 4], [5, 5], [3, 9]],
  KE: [[34, 4], [40, 5], [41, 2], [40, -1], [39, -4], [34, -2]],
  ET: [[34, 14], [40, 14], [44, 12], [43, 8], [38, 4], [34, 7]],
  CD: [[12, 5], [24, 4], [29, 5], [30, -2], [28, -8], [20, -8], [15, -5], [12, -2]],
  TZ: [[30, -1], [40, -1], [40, -7], [37, -11], [30, -11], [30, -5]],
  AO: [[12, -5], [20, -7], [22, -10], [22, -17], [14, -17], [12, -13]],
  MA: [[-13, 36], [-1, 36], [0, 33], [-5, 29], [-13, 28]],
  TN: [[8, 37], [11, 37], [12, 33], [10, 30], [8, 34]],
  CN: [[75, 40], [80, 45], [90, 48], [100, 50], [112, 50], [120, 52], [133, 48], [131, 45], [127, 42], [125, 40], [122, 35], [120, 32], [122, 29], [118, 24], [114, 22], [110, 21], [105, 22], [101, 22], [98, 28], [88, 28], [80, 34], [75, 38]],
  // India outline (~45 points, traced clockwise). Accuracy is good enough
  // that all major Searce cities (Pune, Mumbai, Bangalore, Hyderabad,
  // Gurgaon, Chennai, Kolkata, Ahmedabad, Delhi/Noida) sit on or inside
  // the shape. Kashmir + NE arm are included; Andamans are not.
  IN: [
    [74.0, 35.0],   // Kashmir NW
    [76.0, 35.5],   // Siachen / Karakoram
    [78.0, 35.5],   // Ladakh top
    [78.7, 34.5],   // Aksai Chin
    [80.0, 32.5],   // Spiti / Tibet border
    [81.5, 30.5],   // Uttarakhand
    [83.5, 28.5],   // Nepal border (west)
    [86.0, 27.5],   // Nepal border (east)
    [88.0, 27.0],   // Sikkim
    [89.5, 26.5],   // Bhutan W foothills
    [90.5, 27.0],   // Bhutan
    [92.0, 27.5],   // Arunachal W
    [94.0, 27.7],   // Arunachal central
    [96.5, 28.5],   // Far NE tip
    [97.0, 27.5],   // start coming back south
    [96.0, 26.5],   // Nagaland N
    [95.0, 25.5],   // Manipur
    [94.0, 24.0],   // Mizoram N
    [93.0, 23.0],   // Mizoram S
    [92.5, 22.0],   // Tripura
    [91.5, 23.0],   // Bangladesh border
    [89.5, 25.0],   // West Bengal N
    [88.5, 26.5],   // North Bengal
    [88.0, 25.0],   // Bengal interior
    [88.6, 22.6],   // east of Kolkata
    [88.2, 21.5],   // Sundarbans
    [86.8, 21.5],   // Bengal/Odisha coast
    [85.5, 20.0],   // Puri area
    [83.5, 18.5],   // Visakhapatnam region
    [81.5, 16.5],   // Andhra coast
    [80.5, 14.0],   // Nellore area
    [80.3, 13.1],   // Chennai
    [79.8, 11.5],   // Cuddalore
    [79.0, 10.0],   // Tamil Nadu mid-coast
    [78.2, 8.8],    // Tuticorin
    [77.5, 8.1],    // Kanyakumari (southern tip)
    [76.5, 9.0],    // Kerala south
    [75.5, 11.0],   // Calicut
    [74.7, 13.0],   // Mangalore
    [74.2, 15.0],   // Goa
    [73.2, 17.0],   // Konkan
    [72.8, 19.0],   // Mumbai
    [72.6, 20.7],   // Daman
    [72.6, 21.7],   // Surat / Gujarat
    [71.0, 21.0],   // Saurashtra south
    [69.5, 22.0],   // Saurashtra W
    [68.5, 23.5],   // Bhuj / Rann of Kutch
    [69.5, 24.5],   // Sindh border
    [70.5, 26.5],   // Rajasthan W
    [72.5, 28.5],   // Punjab border
    [74.5, 30.5],   // Punjab N
    [75.5, 32.5],   // Himachal Pradesh
    [74.0, 35.0],   // back to start
  ],
  JP: [[130, 32], [133, 34], [140, 37], [141, 42], [145, 44], [143, 42], [141, 40], [139, 36], [136, 35], [133, 33]],
  KR: [[125, 38], [129, 38], [129, 35], [127, 34], [126, 35]],
  ID: [[95, 5], [105, 3], [115, 1], [125, 0], [135, -2], [140, -4], [138, -8], [125, -9], [115, -9], [105, -8], [100, -3]],
  MY: [[100, 7], [104, 5], [110, 3], [115, 5], [118, 6], [113, 4], [103, 4]],
  TH: [[98, 20], [100, 18], [103, 15], [102, 12], [100, 7], [98, 8], [98, 15]],
  VN: [[103, 22], [107, 22], [108, 18], [109, 12], [105, 8], [103, 10], [105, 17]],
  PH: [[120, 18], [122, 16], [124, 14], [125, 10], [122, 6], [120, 9], [119, 12]],
  PK: [[61, 29], [66, 30], [72, 33], [76, 37], [74, 32], [68, 26], [63, 26]],
  BD: [[88, 26], [92, 26], [92, 22], [88, 22]],
  AF: [[60, 37], [70, 37], [74, 38], [72, 32], [66, 30], [61, 32]],
  KZ: [[47, 55], [55, 56], [70, 55], [80, 50], [83, 47], [70, 42], [55, 42], [50, 45]],
  MN: [[88, 52], [110, 52], [120, 48], [110, 42], [95, 42], [88, 48]],
  AU: [[114, -22], [122, -18], [130, -12], [136, -12], [140, -17], [145, -15], [151, -25], [153, -28], [150, -37], [143, -39], [135, -35], [125, -32], [115, -32]],
  NZ: [[170, -35], [175, -37], [178, -39], [174, -42], [171, -44], [167, -46], [170, -40]],
  PG: [[141, -3], [150, -4], [155, -6], [150, -10], [141, -9]],
  SG: [[103.6, 1.45], [104.0, 1.45], [104.0, 1.2], [103.6, 1.2]],
};

// -------------------------------------------------- country → readable label

export const COUNTRY_LABELS: Record<string, string> = {
  US: 'United States',
  IN: 'India',
  SG: 'Singapore',
  JP: 'Japan',
  AE: 'United Arab Emirates',
  AU: 'Australia',
  GB: 'United Kingdom',
  CA: 'Canada',
  DE: 'Germany',
  FR: 'France',
  IT: 'Italy',
  ES: 'Spain',
  NL: 'Netherlands',
  BR: 'Brazil',
  MX: 'Mexico',
};

// --------------------------------------- approximate country centroid (lon, lat)

export const COUNTRY_CENTROID: Record<string, LonLat> = {
  US: [-98, 39],
  IN: [79, 22],
  SG: [103.8, 1.35],
  JP: [138, 36],
  AE: [54, 24],
  AU: [134, -25],
  GB: [-2, 54],
  CA: [-105, 56],
  DE: [10, 51],
  FR: [2, 47],
  IT: [12, 42],
  ES: [-3, 40],
  NL: [5.5, 52],
  BR: [-53, -10],
  MX: [-102, 23],
  CN: [104, 36],
  ID: [113, -2],
  RU: [100, 60],
};

// --------------------------------------- city / location → coordinates (lon, lat)
//
// Backend HR data emits free-form location strings (e.g. "Pune", "Bengaluru").
// We map the well-known ones below; anything else falls through and appears
// in the "Unmapped" list under the map. Add more rows here as they show up.

export const LOCATION_COORDS: Record<string, LonLat> = {
  // India (verified against Wikipedia / Google)
  pune: [73.8567, 18.5204],
  mumbai: [72.8777, 19.076],
  bombay: [72.8777, 19.076],
  bangalore: [77.5946, 12.9716],
  bengaluru: [77.5946, 12.9716],
  hyderabad: [78.4867, 17.385],
  secunderabad: [78.5037, 17.4399],
  gurgaon: [77.0266, 28.4595],
  gurugram: [77.0266, 28.4595],
  chennai: [80.2707, 13.0827],
  madras: [80.2707, 13.0827],
  delhi: [77.1025, 28.7041],
  'new delhi': [77.209, 28.6139],
  noida: [77.391, 28.5355],
  'greater noida': [77.504, 28.4744],
  ahmedabad: [72.5714, 23.0225],
  kolkata: [88.3639, 22.5726],
  calcutta: [88.3639, 22.5726],
  kochi: [76.2673, 9.9312],
  cochin: [76.2673, 9.9312],
  thiruvananthapuram: [76.9366, 8.5241],
  trivandrum: [76.9366, 8.5241],
  coimbatore: [76.9558, 11.0168],
  mysuru: [76.6394, 12.2958],
  mysore: [76.6394, 12.2958],
  vadodara: [73.1812, 22.3072],
  surat: [72.8311, 21.1702],
  indore: [75.8577, 22.7196],
  bhopal: [77.4126, 23.2599],
  jaipur: [75.7873, 26.9124],
  lucknow: [80.9462, 26.8467],
  chandigarh: [76.7794, 30.7333],
  visakhapatnam: [83.2185, 17.6868],
  vizag: [83.2185, 17.6868],

  // North America
  'san francisco': [-122.42, 37.77],
  sf: [-122.42, 37.77],
  'new york': [-74.0, 40.71],
  nyc: [-74.0, 40.71],
  austin: [-97.74, 30.27],
  chicago: [-87.63, 41.88],
  seattle: [-122.33, 47.61],
  boston: [-71.06, 42.36],
  atlanta: [-84.39, 33.75],
  toronto: [-79.38, 43.65],

  // APAC
  singapore: [103.8, 1.35],
  tokyo: [139.69, 35.68],
  osaka: [135.5, 34.69],
  sydney: [151.2, -33.86],
  melbourne: [144.96, -37.81],
  manila: [120.98, 14.6],
  jakarta: [106.83, -6.21],

  // EMEA
  dubai: [55.27, 25.2],
  'abu dhabi': [54.37, 24.45],
  london: [-0.12, 51.5],
  paris: [2.35, 48.86],
  amsterdam: [4.9, 52.37],
  berlin: [13.4, 52.52],
  madrid: [-3.7, 40.42],
};

/** Case-insensitive lookup of a free-form location string to [lon, lat]. */
export function coordsForLocation(name: string): LonLat | null {
  const key = name.trim().toLowerCase();
  if (!key) return null;
  return LOCATION_COORDS[key] ?? null;
}

// Aliases used by both `centroidForCountry` and `countryCodeForName`.
const COUNTRY_NAME_ALIASES: Record<string, string> = {
  india: 'IN',
  'united states': 'US',
  usa: 'US',
  'u.s.a.': 'US',
  'us of a': 'US',
  america: 'US',
  uk: 'GB',
  'great britain': 'GB',
  england: 'GB',
  uae: 'AE',
  'u.a.e.': 'AE',
  singapore: 'SG',
  japan: 'JP',
  australia: 'AU',
  canada: 'CA',
  germany: 'DE',
  france: 'FR',
  italy: 'IT',
  spain: 'ES',
  netherlands: 'NL',
  brazil: 'BR',
  mexico: 'MX',
};

/**
 * Resolve a free-form country string to an ISO-2 code that matches the
 * keys of `COUNTRY_POLYS` / `COUNTRY_LABELS` / `COUNTRY_CENTROID`.
 */
export function countryCodeForName(name: string): string | null {
  const trimmed = name.trim();
  if (!trimmed) return null;
  // Direct ISO match.
  const upper = trimmed.toUpperCase();
  if (COUNTRY_POLYS[upper]) return upper;
  // Formal label match.
  for (const [code, label] of Object.entries(COUNTRY_LABELS)) {
    if (label.toLowerCase() === trimmed.toLowerCase()) return code;
  }
  // Alias match.
  const alias = COUNTRY_NAME_ALIASES[trimmed.toLowerCase()];
  if (alias && COUNTRY_POLYS[alias]) return alias;
  return null;
}

/** Approximate centroid for a free-form country string. */
export function centroidForCountry(name: string): LonLat | null {
  const code = countryCodeForName(name);
  return code ? COUNTRY_CENTROID[code] ?? null : null;
}

/** Polygon points for a free-form country string (or null when unknown). */
export function polygonForCountry(name: string): LonLat[] | null {
  const code = countryCodeForName(name);
  return code ? COUNTRY_POLYS[code] ?? null : null;
}
